import { get } from 'lodash'

/**
 * Determines the consent status for a given type of data.
 *
 * @param {Object} data - The consent data object containing various consent
 * types.
 * @param {string} type - The type of consent to check (e.g., 'marketing',
 * 'performance').
 * @return {string} - Returns 'granted' if consent for the type is true,
 * otherwise 'denied'.
 */
const getConsentStatus = (data, type) => {
  return data[type] ? 'granted' : 'denied'
}

/**
 * Returns geo specific data for a given value.
 *
 * @param {Object} data - The consent data object containing various consent
 * types.
 * @param {string} name - The field name of geo data.
 * @return {string} - Returns value for specific geo field data .
 */
const getGeoData = (data, name) => get(data, `geo.${name}`, null)

/**
 * Returns payload for the Google Tag Manager (GTM) consent status based
 * on the provided consent data.
 *
 * The `data` object should have the following structure
 * (it comes from CookieCat):
 * {
 *   essential: Boolean, // Indicates if essential cookies are allowed.
 *   functionality: Boolean, // Indicates if functionality cookies are allowed.
 *   marketing: Boolean, // Indicates if marketing cookies are allowed.
 *   performance: Boolean, // Indicates if performance cookies are allowed.
 * }
 *
 * The consent status for each category is determined as follows:
 * - `granted` if the corresponding value in the `data` object is `true`.
 * - `denied` if the corresponding value in the `data` object is `false`.
 *
 * This function maps the consent status to specific GTM consent settings:
 * - `ad_personalization`, `ad_storage`, `ad_user_data` are set
 * based on the `marketing` consent.
 * - `analytics_storage` is set based on the `performance` consent.
 * - `functionality_storage` is set based on the `functionality` consent.
 * - `security_storage` is set based on the `essential` consent.
 *
 * @param {Object} data - The consent data object containing various consent
 * types.
 * @return {Object} - An object mapping GTM consent variables to 'granted' or
 * 'denied'.
 */
export const updateGTMConsentStatus = data => {
  return {
    ad_personalization: getConsentStatus(data, 'marketing'),
    ad_storage: getConsentStatus(data, 'marketing'),
    ad_user_data: getConsentStatus(data, 'marketing'),
    analytics_storage: getConsentStatus(data, 'performance'),
    country: getGeoData(data, 'country'),
    functionality_storage: getConsentStatus(data, 'essential'),
    is_eu_country: getGeoData(data, 'isEUCountry'),
    personalization_storage: getConsentStatus(data, 'functionality'),
    region_code: getGeoData(data, 'regionCode'),
    security_storage: getConsentStatus(data, 'essential')
  }
}
